import {
  Children, isValidElement, useEffect, useMemo, useRef, useState,
  type CSSProperties, type ReactNode,
} from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, X, Search as SearchIcon } from 'lucide-react';
import { cn } from './cn';
import { Spinner } from './Button';
import { useFormContext } from './Form';
import type { ControlSize } from './Input';

type Val = string | number;

export interface SelectOption {
  value: Val;
  label?: ReactNode;
  disabled?: boolean;
  title?: string;
  children?: ReactNode; // when authored as <Select.Option>text</Select.Option>
}

export interface SelectProps {
  value?: Val | Val[] | null;
  defaultValue?: Val | Val[];
  onChange?: (value: any, option?: any) => void;
  options?: (SelectOption | { label?: ReactNode; options: SelectOption[] })[];
  children?: ReactNode;
  placeholder?: ReactNode;
  disabled?: boolean;
  size?: ControlSize | 'small' | 'middle' | 'large';
  invalid?: boolean;
  status?: 'error' | 'warning';
  className?: string;
  style?: CSSProperties;
  allowClear?: boolean;
  showSearch?: boolean;
  mode?: 'multiple' | 'tags';
  loading?: boolean;
  optionFilterProp?: string;
  /** antd: which option field to render as the selected value (e.g. "text"). Defaults to label. */
  optionLabelProp?: string;
  /** antd: custom render for each dropdown option (receives the raw option). */
  optionRender?: (option: any, info?: { index: number }) => ReactNode;
  filterOption?: boolean | ((input: string, option: SelectOption) => boolean);
  notFoundContent?: ReactNode;
  maxTagCount?: number | 'responsive';
  onSearch?: (v: string) => void;
  onClear?: () => void;
  popupMatchSelectWidth?: boolean;
  dropdownStyle?: CSSProperties;
  autoFocus?: boolean;
  id?: string;
  'aria-label'?: string;
  /** tags mode: characters that commit a token (Enter always commits). */
  tokenSeparators?: string[];
  /** antd: force the dropdown open/closed. Only `false` (never open) is honored. */
  open?: boolean;
  /** antd: custom suffix; `null` hides the chevron. */
  suffixIcon?: ReactNode | null;
}

const SIZE: Record<ControlSize, string> = { sm: 'min-h-8 text-[13px]', md: 'min-h-9 text-sm', lg: 'min-h-11 text-[15px]' };
const normSize = (s?: SelectProps['size']): ControlSize => (s === 'small' ? 'sm' : s === 'middle' ? 'md' : s === 'large' ? 'lg' : (s as ControlSize) ?? 'md');

const labelText = (o: SelectOption): string => {
  const l = o.label ?? o.children;
  return typeof l === 'string' || typeof l === 'number' ? String(l) : String(o.value);
};

// Extract <Select.Option>/<Select.OptGroup> children into an options array.
function fromChildren(children: ReactNode): SelectOption[] {
  const out: SelectOption[] = [];
  Children.forEach(children, (c) => {
    if (!isValidElement(c)) return;
    const p: any = c.props;
    if (p && p.value !== undefined) out.push({ value: p.value, label: p.label ?? p.children, disabled: p.disabled });
    else if (p && p.children) out.push(...fromChildren(p.children)); // OptGroup
  });
  return out;
}

function OptionComp(_props: { value: Val; label?: ReactNode; disabled?: boolean; children?: ReactNode }) { return null; }
function OptGroup(_props: { label?: ReactNode; children?: ReactNode }) { return null; }

/**
 * antd `Select mode="tags"` — free-form token input: type + Enter (or a tokenSeparator)
 * to add a value not in any options list, chips are removable. No dropdown when `open={false}`.
 */
function SelectTags({ value, onChange, placeholder, disabled, size, className, style, allowClear, suffixIcon, tokenSeparators, invalid, status, id }: SelectProps) {
  const [text, setText] = useState('');
  const vals: Val[] = Array.isArray(value) ? value : value == null ? [] : [value];
  const sz = normSize(size);
  const isErr = invalid || status === 'error';
  const commit = (raw: string) => {
    const t = raw.trim();
    if (t && !vals.some((v) => String(v) === t)) onChange?.([...vals, t], undefined);
    setText('');
  };
  const removeAt = (i: number) => onChange?.(vals.filter((_, idx) => idx !== i), undefined);
  const seps = tokenSeparators ?? [];
  return (
    <div
      style={style}
      onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus()}
      className={cn(
        'flex w-full flex-wrap items-center gap-1 rounded-control border bg-surface px-2 py-1 text-content transition-colors focus-within:ring-2 focus-within:ring-brand-500/30',
        isErr ? 'border-danger-500' : 'border-line focus-within:border-brand-400',
        disabled && 'cursor-not-allowed opacity-60',
        SIZE[sz], className,
      )}
    >
      {vals.map((v, i) => (
        <span key={`${String(v)}-${i}`} className="inline-flex items-center gap-1 rounded bg-surface-3 px-1.5 py-0.5 text-[12px] text-content">
          {String(v)}
          {!disabled && (
            <span role="button" tabIndex={-1} aria-label="Remove" onClick={(e) => { e.stopPropagation(); removeAt(i); }} className="text-subtle hover:text-content">
              <X className="size-3" />
            </span>
          )}
        </span>
      ))}
      <input
        id={id}
        value={text}
        disabled={disabled}
        placeholder={vals.length ? '' : (typeof placeholder === 'string' ? placeholder : '')}
        onChange={(e) => {
          const val = e.target.value;
          if (seps.length && seps.some((s) => val.includes(s))) {
            const parts = val.split(new RegExp(seps.map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')));
            parts.slice(0, -1).forEach(commit);
            setText(parts[parts.length - 1] ?? '');
          } else setText(val);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(text); }
          else if (e.key === 'Backspace' && text === '' && vals.length) removeAt(vals.length - 1);
        }}
        className="min-w-[60px] flex-1 bg-transparent text-sm outline-none placeholder:text-subtle"
      />
      {suffixIcon !== null && allowClear && vals.length > 0 && !disabled && (
        <span role="button" tabIndex={-1} aria-label="Clear" onClick={(e) => { e.stopPropagation(); onChange?.([], undefined); }} className="text-subtle hover:text-content">
          <X className="size-4" />
        </span>
      )}
    </div>
  );
}

/**
 * Ant Design `Select` replacement — single OR multiple, optional search, clearable.
 * Backward-compatible with the earlier single-select ui API (value:string, onChange:(v)=>void).
 * Built on Radix Popover (portalled, positioned, focus-safe).
 */
function SelectBase(props: SelectProps) {
  // tags mode is a free-form token input — a fully separate, self-contained render path.
  if (props.mode === 'tags') return <SelectTags {...props} />;
  return <SelectSingleOrMultiple {...props} />;
}

function SelectSingleOrMultiple({
  value, onChange, options, children, placeholder = 'Select', disabled, size, invalid, status,
  className, style, allowClear, showSearch, mode, loading, optionFilterProp, optionLabelProp, optionRender, filterOption,
  notFoundContent = 'No results', maxTagCount, onSearch, onClear, id, tokenSeparators, open: _openProp, suffixIcon, ...aria
}: SelectProps) {
  void tokenSeparators; void _openProp; void suffixIcon;
  // antd parity: a Select fills its Form.Item inside a form, but is content-sized (auto width,
  // honoring an explicit style.width/minWidth) when standalone — e.g. in a wrap-flex filter bar.
  // Callers can always force a width via `style` or a `w-…` className.
  const inForm = useFormContext() != null;
  const widthCls = inForm ? 'w-full' : 'w-auto min-w-[7rem] max-w-full';
  // The label shown for a SELECTED value: `optionLabelProp` (antd) wins, else label/children/value.
  const chipLabel = (o: SelectOption): ReactNode =>
    (optionLabelProp ? (o as any)[optionLabelProp] : undefined) ?? o.label ?? o.children ?? String(o.value);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const multiple = mode === 'multiple' || mode === 'tags';
  const sz = normSize(size);
  const isErr = invalid || status === 'error';

  const opts = useMemo<SelectOption[]>(() => {
    const raw = options ?? fromChildren(children);
    const flat: SelectOption[] = [];
    for (const o of raw as any[]) {
      if (o && Array.isArray(o.options)) flat.push(...o.options); // antd OptGroup { label, options }
      else flat.push(o);
    }
    return flat;
  }, [options, children]);
  const byVal = useMemo(() => new Map(opts.map((o) => [String(o.value), o])), [opts]);

  const selected: Val[] = value == null ? [] : Array.isArray(value) ? value : [value];

  const filtered = useMemo(() => {
    if (!showSearch || !query) return opts;
    const q = query.toLowerCase();
    if (typeof filterOption === 'function') return opts.filter((o) => filterOption(query, o));
    if (filterOption === false) return opts;
    return opts.filter((o) => {
      const hay = optionFilterProp === 'value'
        ? String(o.value)
        : optionFilterProp && optionFilterProp !== 'label' && (o as any)[optionFilterProp] != null
          ? String((o as any)[optionFilterProp])
          : `${labelText(o)} ${o.value}`;
      return hay.toLowerCase().includes(q);
    });
  }, [opts, query, showSearch, filterOption, optionFilterProp]);

  useEffect(() => { if (open && showSearch) setTimeout(() => searchRef.current?.focus(), 10); if (!open) { setQuery(''); setHi(0); } }, [open, showSearch]);

  const pick = (o: SelectOption) => {
    if (o.disabled) return;
    if (multiple) {
      const has = selected.some((v) => String(v) === String(o.value));
      const next = has ? selected.filter((v) => String(v) !== String(o.value)) : [...selected, o.value];
      onChange?.(next, next.map((v) => byVal.get(String(v)) ?? { value: v }));
      setQuery('');
    } else {
      onChange?.(o.value, o);
      setOpen(false);
    }
  };
  const clear = (e: React.MouseEvent) => { e.stopPropagation(); onChange?.(multiple ? [] : undefined, undefined); onClear?.(); };

  const hasValue = selected.length > 0;
  const shownTags = maxTagCount === 'responsive' ? 2 : typeof maxTagCount === 'number' ? maxTagCount : selected.length;

  return (
    <Popover.Root open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <Popover.Trigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-label={aria['aria-label']}
          style={style}
          className={cn(
            'group flex items-center gap-2 rounded-control border bg-surface px-3 py-1 text-left text-content transition-colors',
            widthCls,
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:cursor-not-allowed disabled:opacity-50',
            isErr ? 'border-danger-500' : status === 'warning' ? 'border-warning-500' : 'border-line hover:border-brand-400',
            SIZE[sz], className,
          )}
        >
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1 py-0.5">
            {!hasValue && <span className="truncate text-subtle">{placeholder}</span>}
            {multiple
              ? selected.slice(0, shownTags).map((v) => {
                  const o = byVal.get(String(v));
                  return (
                    <span key={String(v)} className="inline-flex items-center gap-1 rounded bg-surface-3 px-1.5 py-0.5 text-[12px] text-content">
                      {o ? chipLabel(o) : String(v)}
                      <span role="button" tabIndex={-1} aria-label="Remove" onClick={(e) => { e.stopPropagation(); pick(o ?? { value: v }); }} className="text-subtle hover:text-content">
                        <X className="size-3" />
                      </span>
                    </span>
                  );
                })
              : hasValue && <span className="truncate">{(() => { const o = byVal.get(String(selected[0])); return o ? chipLabel(o) : String(selected[0]); })()}</span>}
            {multiple && selected.length > shownTags && <span className="text-xs text-muted">+{selected.length - shownTags}</span>}
          </span>
          {loading && <Spinner className="size-3.5 text-muted" />}
          {allowClear && hasValue && !disabled && (
            <span role="button" tabIndex={-1} aria-label="Clear" onClick={clear} className="hidden shrink-0 text-subtle hover:text-content group-hover:inline-flex">
              <X className="size-4" />
            </span>
          )}
          <ChevronDown className="size-4 shrink-0 text-muted" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={5}
          className="z-[var(--z-popover)] max-h-[300px] w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-card border border-line bg-surface p-0 shadow-pop"
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, filtered.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
            else if (e.key === 'Enter') { e.preventDefault(); if (filtered[hi]) pick(filtered[hi]); }
          }}
        >
          {showSearch && (
            <div className="flex items-center gap-2 border-b border-line px-3 py-2">
              <SearchIcon className="size-4 shrink-0 text-subtle" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setHi(0); onSearch?.(e.target.value); }}
                placeholder="Search…"
                className="w-full bg-transparent text-sm text-content outline-none placeholder:text-subtle"
              />
            </div>
          )}
          <div className="max-h-[248px] overflow-y-auto p-1.5" role="listbox">
            {filtered.length === 0 ? (
              <div className="px-2.5 py-6 text-center text-sm text-subtle">{notFoundContent}</div>
            ) : (
              filtered.map((o, i) => {
                const sel = selected.some((v) => String(v) === String(o.value));
                return (
                  <div
                    key={String(o.value)}
                    role="option"
                    aria-selected={sel}
                    onMouseEnter={() => setHi(i)}
                    onClick={() => pick(o)}
                    className={cn(
                      'flex cursor-pointer select-none items-center justify-between gap-2 rounded-[10px] px-2.5 py-2 text-sm text-content',
                      o.disabled && 'pointer-events-none opacity-40',
                      i === hi && 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300',
                      sel && 'font-semibold',
                    )}
                  >
                    <span className="truncate">{optionRender ? optionRender(o, { index: i }) : (o.label ?? o.children ?? String(o.value))}</span>
                    {sel && <Check className="size-4 shrink-0 text-brand-600" />}
                  </div>
                );
              })
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export const Select = Object.assign(SelectBase, { Option: OptionComp, OptGroup });
